import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useEffect, useMemo, useState } from "@/common/hooks";
import {
  useGetInvestmentVehiclesQuery,
  useGetManagementCompaniesQuery,
  useGetProductManufacturerInvestmentVehiclesQuery,
  useSaveProductManufacturerInvestmentVehiclesMutation,
} from "@/service/AdminService";
import {
  Button,
  ErrorMessage,
  Spinner,
  SuccessPopup,
  AgGrid,
  Heading,
  SelectDropdown,
  MobileResponsiveGrid,
} from "@/common/components";
import {
  getCurrentDate,
  getFormattedDate,
  scrollToTop,
} from "@/utils/Utilities";

const ProductManufacturerInvestmentVehicles = () => {
  const {
    setNavHeader,
    remove,
    location,
    cancel,
    labels,
    setPageHeader,
    cloneDeep,
    uniqBy,
    setNavCancelMessage,
  } = useContext(PrivateContext);

  const [rowDataForAll, setRowDataForAll] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [selectedVehicleFromAll, setSelectedVehicleFromAll] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState([]);
  const [company, setCompany] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [errors, setErrors] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [gridApiForAll, setGridApiForAll] = useState({});
  
  const [gridApiSelected, setGridApiSelected] = useState({});

  const columnDefsForAll = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      flex: 0.25,
    },
    {
      headerName:
        labels?.productManufacturer?.investmentManagementCompanyHeader,
      field: "investmentManagementCompany.name",
      flex: 1.5,
    },
    {
      headerName: labels?.productManufacturer?.investmentVehicleHeader,
      field: "name",
      flex: 1.8,
    },
    {
      headerName: labels?.productManufacturer?.extVehicleHeader,
      field: "externalId",
    },
    {
      headerName: labels?.productManufacturer?.tradingSymbolHeader,
      field: "tradingSymbol",
    },
    {
      headerName: labels?.productManufacturer?.date,
      field: "targetFundActivationDate",
      valueGetter: (params) =>
        params.data?.currentDate || params.data?.lastMntDateTime,
      hide: true,
      cellRenderer: getFormattedDate,
    },
    {
      headerName:
        labels?.productManufacturer?.targetDateVehicleActivationHeader,
      field: "targetFundActivationDate",
      cellRenderer: getFormattedDate,
    },
  ];

  const columnDefs = cloneDeep(columnDefsForAll).map((e) => {
    if (e.field === "targetFundActivationDate") {
      e.hide = false;
      e.cellRenderer = getFormattedDate;
    }
    return e;
  });

  const {
    data: allManagementCompanies,
    isFetching: isFetching,
    error,
  } = useGetManagementCompaniesQuery();

  const {
    data: allInvestmentVehicles,
    isFetching: isFetching1,
    error: error1,
  } = useGetInvestmentVehiclesQuery(company);

  const {
    data: investmentVehicles,
    isFetching: isFetching2,
    error: error2,
  } = useGetProductManufacturerInvestmentVehiclesQuery({
    productSponsorId: location?.state?.id,
  });

  const [
    SaveProductManufacturerInvestmentVehiclesApi,
    { isLoading: isFetching3, error: error3 },
  ] = useSaveProductManufacturerInvestmentVehiclesMutation();

  const onSelectionChangedForAll = (e) => {
    const selectedRows = e.api.getSelectedRows();
    let array = selectedRows.map((e) => e.id);

    setSelectedVehicleFromAll([...array]);
    setSelectedVehicle([]);
  };

  const onGridReadyForAll = (params) => {
    setGridApiForAll(params.api);
  };

  const onSelectionChanged = (e) => {
    const selectedRows = e.api.getSelectedRows();
    let array = selectedRows.map((e) => e.id);

    setSelectedVehicle([...array]);
  };

  const addVehicles = () => {
    let allData = cloneDeep(rowDataForAll);
    let currentData = cloneDeep(rowData);

    selectedVehicleFromAll.forEach((e) => {
      const vehicle = allData.find((e1) => e1.id === e);
      vehicle.currentDate = getCurrentDate();
      remove(allData, (e1) => e1.id === vehicle.id);
      currentData.push(vehicle);
    });

    setRowDataForAll([...allData]);
    setRowData([...currentData]);
    setFilterBy("");
  };

  const removeVehicle = () => {
    setErrors(null);
    let currentData = cloneDeep(rowData);
    let allData = [];

    for (let i = 0; i < selectedVehicle.length; i++) {
      const vehicle = currentData.find((e1) => e1.id === selectedVehicle[i]);

      if (vehicle.isRemovable === "false") {
        setErrors({
          errorMessage: labels?.productManufacturer?.investmentErrorMessage,
        });
        scrollToTop();
        return;
      }
    }

    selectedVehicle.forEach((e) => {
      const vehicle = currentData.find((e1) => e1.id === e);

      remove(currentData, (e1) => e1.id === vehicle.id);
      allData.push(vehicle);
    });

    setRowData([...currentData]);

    if (company) {
      let array = [];
      allData.forEach((e) => {
        if (e?.investmentManagementCompany?.id === company) {
          array.push(e);
        }
      });
      setRowDataForAll([...rowDataForAll, ...array]);
    } else {
      setRowDataForAll([...rowDataForAll, ...allData]);
    }
  };

  const updateVehicles = () => {
    scrollToTop();

    const payload = {
      productSponsorId: location?.state?.id,
      productSponsorInvestmentVehicleDTOBeans: rowData.map((e) => {
        return {
          investmentVehicleDTOBean: { id: e.id },
        };
      }),
    };

    SaveProductManufacturerInvestmentVehiclesApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  const toggleMobileSelect = (id) => {
    setSelectedVehicle((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // RIGHT list (Selected vehicles)
  const mobileColumnDefsSelected = useMemo(() => {
    return columnDefs.map((col) => {
      if (col.field === "investmentManagementCompany.name") {
        return {
          ...col,
          cellRenderer: (params) => (
            <div className="mr-imc-cell">
              {/* absolutely-positioned checkbox will sit over the left gutter */}
              <input
                type="checkbox"
                className="form-check-input mr-row-check mt-4"
                checked={selectedVehicle.includes(params.data.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleMobileSelect(params.data.id);
                }}
                aria-label={`Select ${params.value}`}
              />
              <span className="mr-imc-text">{params.value}</span>
            </div>
          ),
        };
      }
      return col;
    });
  }, [columnDefs, selectedVehicle]);

  // For left mobile grid (All vehicles) — allow selecting rows by id
  const toggleMobileSelectFromAll = (id) => {
    setSelectedVehicleFromAll((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const mobileColumnDefsAll = useMemo(() => {
    return columnDefsForAll.map((col) => {
      if (col.field === "investmentManagementCompany.name") {
        return {
          ...col,
          cellRenderer: (params) => (
            <div className="mr-imc-cell">
              <input
                type="checkbox"
                className="form-check-input mr-row-check mt-4"
                checked={selectedVehicleFromAll.includes(params.data.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleMobileSelectFromAll(params.data.id);
                }}
                aria-label={`Select ${params.value}`}
              />
              <span className="mr-imc-text">{params.value}</span>
            </div>
          ),
        };
      }
      return col;
    });
  }, [columnDefsForAll, selectedVehicleFromAll]);

  // Replace existing getRowClassSelected with this tolerant version:
const getRowClassSelected = (rowOrParams) => {
  // MobileResponsiveGrid calls getRowClass({ data: entity })
  // Desktop code sometimes passes the row data directly
  const data =
    // if called with params object: { data: entity, ... }
    (rowOrParams && rowOrParams.data) ||
    // if called with { id, ... } or directly the row data
    rowOrParams ||
    {};

  // Try multiple places to read an id (supports nested shapes)
  const id =
    data?.id ??
    data?.data?.id ??
    // fallback: if entire param is just an object with id
    (typeof rowOrParams === "object" ? rowOrParams?.id : null);

  // selectedVehicle is your array of selected ids for the right grid
  const selectedIds = Array.isArray(selectedVehicle) ? selectedVehicle : [];

  return `mr-row-rel ${id != null && selectedIds.includes(id) ? "mr-row-selected" : ""}`;
};

const getRowClassForAll = (rowOrParams) => {
  const data = (rowOrParams && rowOrParams.data) || rowOrParams || {};
  const id = data?.id ?? null;
  const selectedIds = Array.isArray(selectedVehicleFromAll) ? selectedVehicleFromAll : [];
  return `mr-row-rel ${id != null && selectedIds.includes(id) ? "mr-row-selected" : ""}`;
};


  useEffect(() => {
    if (rowDataForAll.length) {
      gridApiForAll.getDisplayedRowAtIndex(0).setSelected(true);
    }
  }, [rowDataForAll]);

  useEffect(() => {
    if (
      allInvestmentVehicles &&
      allInvestmentVehicles?.investementVehicles &&
      investmentVehicles &&
      investmentVehicles?.productSponsorInvestmentVehicleDTOBeans
    ) {
      const vehicles =
        investmentVehicles?.productSponsorInvestmentVehicleDTOBeans?.map(
          (e) => {
            return {
              ...e.investmentVehicleDTOBean,
              isRemovable: e.isRemovable,
              lastMntDateTime: e.lastMntDateTime,
            };
          }
        );
      const totalData = [...rowData, ...vehicles];
      setRowData(uniqBy(totalData, (e) => e.id));
      const filteredVehicles =
        allInvestmentVehicles?.investementVehicles.filter(
          (e) => !totalData.find((e1) => e.id === e1.id)
        );
      
      setRowDataForAll(filteredVehicles);
    }
  }, [allInvestmentVehicles, investmentVehicles]);

  useEffect(() => {
    setNavHeader(labels?.productManufacturer?.productManufacturer);
    setPageHeader(
      labels?.productManufacturer?.investmentVehiclesFor + location?.state?.name
    );
    setNavCancelMessage(
      labels?.productManufacturer?.investmentVehicleCancelMessage
    );
  }, []);

  const overlayNoRowsTemplate = useMemo(
    () =>
      `<div class="ag-overlay-no-rows-center">${
        labels?.productManufacturer?.noDataInvestmentVechile ?? "No data"
      }</div>`,
    [labels]
  );

  const onGridReadySelected = (params) => {
    setGridApiSelected(params.api);
  };

  // When selectedVehicleFromAll (left/mobile selection) changes, select matching rows in left AgGrid
  useEffect(() => {
    try {
      if (!gridApiForAll || !gridApiForAll.forEachNode) return;
      // iterate all nodes and select those whose id is in selectedVehicleFromAll
      gridApiForAll.forEachNode((node) => {
        const id = node?.data?.id;
        const shouldSelect = Array.isArray(selectedVehicleFromAll)
          ? selectedVehicleFromAll.includes(id)
          : false;
        // only update when different to avoid flicker
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      });
    } catch (e) {
      // defensive: don't crash UI
      console.warn("sync left grid selection failed", e);
    }
  }, [selectedVehicleFromAll, gridApiForAll]);

  // When selectedVehicle (right/mobile selection) changes, select matching rows in right AgGrid
  useEffect(() => {
    try {
      if (!gridApiSelected || !gridApiSelected.forEachNode) return;
      gridApiSelected.forEachNode((node) => {
        const id = node?.data?.id;
        const shouldSelect = Array.isArray(selectedVehicle)
          ? selectedVehicle.includes(id)
          : false;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      });
    } catch (e) {
      console.warn("sync right grid selection failed", e);
    }
  }, [selectedVehicle, gridApiSelected]);

  return (
    <section className="container-x-large py-5">
      {(isFetching || isFetching1 || isFetching2 || isFetching3) && <Spinner />}
      <ErrorMessage
        error={errors || error || error1 || error2 || error3}
        wrapperClassName="mb-2"
      />

      <SelectDropdown
        id="managementCompanies"
        label={labels?.productManufacturer?.investmentManagementCompany}
        dropdownData={allManagementCompanies?.investmentManagementCompanys}
        wrapperClassName="col-md-4 mb-2"
        selectClassName="w-100 py-2"
        select={(e) => setCompany(e)}
        value={company}
        optionId="id"
        optionLabel="name"
        defaultOption={true}
        defaultOptionLabel={"ALL"}
        ariaLabel="Management Companies"
      />

      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        printClassNames="mt-md-n3"
        columnDefs={columnDefsForAll}
        rowSelection="multiple"
        onSelectionChanged={onSelectionChangedForAll}
        onGridReady={onGridReadyForAll}
        rowData={
          filterBy
            ? rowDataForAll.filter((e) =>
                e.companyName.toLowerCase().startsWith(filterBy.toLowerCase())
              )
            : rowDataForAll
        }
        domLayout={"normal"}
      />
      <MobileResponsiveGrid
        key="prodManufacturerInvVehicle1"
        rowData={
          filterBy
            ? rowDataForAll.filter((e) =>
                e.companyName.toLowerCase().startsWith(filterBy.toLowerCase())
              )
            : rowDataForAll
        }
        columnDefs={mobileColumnDefsAll}
        getRowClass={getRowClassForAll}
        selectedRowId={selectedVehicleFromAll} // pass array of ids
        selectedRowIdField="id"
        onSelectionChanged={(ids) => setSelectedVehicleFromAll(ids)}
      />
      <Button
        id="add"
        text={labels?.productManufacturer?.add}
        wrapperClassName="col-md-2 ms-auto mb-5 mt-4"
        click={addVehicles}
        disabled={!selectedVehicleFromAll.length}
      />

      <Heading
        text={labels?.productManufacturer?.investmentVehicles}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3 mt-4"
        title={false}
      />
      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        columnDefs={columnDefs}
        rowData={rowData}
        headerHeight="70"
        domLayout={"normal"}
        onGridReady={onGridReadySelected} // <- add this
        rowSelection="multiple"
        onSelectionChanged={onSelectionChanged}
        overlayNoRowsTemplate={overlayNoRowsTemplate}
      />
      <MobileResponsiveGrid
        key="prodManufacturerInvVehicle2"
        rowData={rowData}
        columnDefs={mobileColumnDefsSelected}
        getRowClass={getRowClassSelected}
        noDataLabel={labels?.productManufacturer?.noDataInvestmentVechile}
        selectedRowId={selectedVehicle} // pass array of ids (selectedVehicle)
        selectedRowIdField="id"
        onSelectionChanged={(ids) => setSelectedVehicle(ids)}
      />

      <Button
        id="remove"
        text={labels?.productManufacturer?.remove}
        wrapperClassName="col-md-2 ms-auto mt-4"
        click={removeVehicle}
        disabled={!selectedVehicle.length}
      />

      <Button
        id="done"
        text={labels?.productManufacturer?.submit}
        wrapperClassName="col-md-2 mx-auto mt-4"
        click={updateVehicles}
      />

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.productManufacturer?.continue}
        click={cancel}
        body={labels?.productManufacturer?.requestCompleted}
      />
    </section>
  );
};

export default ProductManufacturerInvestmentVehicles;
